/**
 * Custom cart products – quantity +/- and input handler.
 *
 * Finds the native cart-items-component in the DOM and calls its public
 * updateQuantity / onLineItemRemove methods directly, so all section
 * morphing, cart-bubble updates, and cart-summary refreshes happen through
 * the existing native pipeline.
 *
 * Rapid clicks / typed values are batched: the UI updates immediately, then a
 * single /cart/change request is sent after a short debounce with the final quantity.
 */
(function () {
  const DEBOUNCE_MS = 600;
  const CAN_LIMIT = 12;

  /** @type {Map<number, number>} Last server-confirmed quantity per line. */
  const confirmedQtyByLine = new Map();
  /** @type {Map<number, number>} Pending target quantity per line. */
  const pendingByLine = new Map();
  /** @type {Map<number, ReturnType<typeof setTimeout>>} */
  const debounceTimers = new Map();
  /** @type {Set<number>} Lines with a cart/change request in flight. */
  const inFlightLines = new Set();
  /** @type {object | null} Latest cart payload from cart-items-component. */
  let lastCartResource = null;
  /** @type {object | null} Cart payload tied to the in-flight /cart/change request. */
  let cartResourceForPendingUpdate = null;

  function getCartComp() {
    return /** @type {any} */ (document.querySelector('cart-items-component'));
  }

  function getCartList() {
    return document.querySelector('cart-items-component[data-drawer] .ccp-list') || document.querySelector('.ccp-list');
  }

  function getItem(line) {
    return document.querySelector(`.ccp-item[data-line="${line}"]`);
  }

  function getQtyInput(item) {
    return item ? item.querySelector('.ccp-item__qty-input') : null;
  }

  function ensureConfirmed(line, item) {
    if (!confirmedQtyByLine.has(line)) {
      confirmedQtyByLine.set(line, parseInt(item.dataset.qty, 10));
    }
  }

  function getCurrentTarget(line, item) {
    if (pendingByLine.has(line)) return pendingByLine.get(line);
    if (confirmedQtyByLine.has(line)) return confirmedQtyByLine.get(line);
    return parseInt(item.dataset.qty, 10);
  }

  function setDisplayedQty(item, qty) {
    item.dataset.qty = String(qty);
    item.querySelectorAll('.ccp-btn').forEach((b) => {
      b.dataset.qty = String(qty);
    });
    const input = getQtyInput(item);
    if (input instanceof HTMLInputElement) {
      input.dataset.qty = String(qty);
      // Don't clobber what the user is actively typing.
      if (document.activeElement !== input) {
        input.value = String(qty);
      }
    }
  }

  function formatLinePrice(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }

    return '$' + (cents / 100).toFixed(2);
  }

  function setLinePrice(item, cents) {
    const priceEl = item.querySelector('.ccp-item__price');
    if (priceEl) priceEl.textContent = formatLinePrice(cents);
  }

  /**
   * Resolve the effective qty for a cart row.
   * Prefers pending → confirmed → data-qty so enforcement never depends on a stale tracker.
   */
  function getLineQty(el, overrideLine, overrideQty) {
    const itemLine = parseInt(el.dataset.line, 10);
    if (itemLine === overrideLine) return overrideQty;
    if (pendingByLine.has(itemLine)) return pendingByLine.get(itemLine);
    if (confirmedQtyByLine.has(itemLine)) return confirmedQtyByLine.get(itemLine);
    return parseInt(el.dataset.qty || '0', 10) || 0;
  }

  function getCartItems() {
    const list = getCartList();
    return list ? list.querySelectorAll('.ccp-item') : document.querySelectorAll('.ccp-item');
  }

  /**
   * Compute prescription grams from live drawer line items (source of truth).
   */
  function computeGramTotalFromDom(overrideLine, overrideQty) {
    let total = 0;
    getCartItems().forEach((el) => {
      const gramDeduction = parseFloat(el.dataset.gramDeduction || '0');
      if (!(gramDeduction > 0)) return;
      total += getLineQty(el, overrideLine, overrideQty) * gramDeduction;
    });
    return total;
  }

  function updateGramTrackerFromDom() {
    const tracker = document.querySelector('.cart__gram-tracker');
    if (!tracker) return;
    tracker.dataset.gramTotal = String(computeGramTotalFromDom());
  }

  function syncGramTrackerFromCart(cart) {
    const tracker = document.querySelector('.cart__gram-tracker');
    if (!tracker) return;

    if (!cart?.items?.length) {
      tracker.dataset.gramTotal = '0';
      return;
    }

    // Prefer DOM line-item math over morph/tracker copy — hydration can leave
    // the summary tracker stale while confirmed qtys are already updated.
    updateGramTrackerFromDom();
  }

  /**
   * Keeps qty/price/line indices in sync after cart API updates.
   * Matches rows by cart line order (not variant id) to avoid mismatches when
   * the same variant appears on multiple lines.
   * @param {{ items?: Array<{ variant_id: number, quantity: number, final_line_price: number }> }} cart
   */
  function syncLineItemsFromCart(cart) {
    if (!cart?.items) return;

    const list = getCartList();
    if (!list) return;

    const rows = list.querySelectorAll('.ccp-item');
    const nextConfirmed = new Map();

    cart.items.forEach((cartItem, index) => {
      const line = index + 1;
      const item = rows[index];

      if (!item) return;

      item.dataset.line = String(line);
      item.dataset.variantId = String(cartItem.variant_id);
      item.querySelectorAll('.ccp-btn').forEach((button) => {
        button.dataset.line = String(line);
      });
      const input = getQtyInput(item);
      if (input) input.dataset.line = String(line);

      setDisplayedQty(item, cartItem.quantity);
      setLinePrice(item, cartItem.final_line_price);
      nextConfirmed.set(line, cartItem.quantity);
      pendingByLine.delete(line);
    });

    confirmedQtyByLine.clear();
    nextConfirmed.forEach((qty, line) => confirmedQtyByLine.set(line, qty));
    syncGramTrackerFromCart(cart);
  }

  function revertLine(line) {
    pendingByLine.delete(line);
    const confirmed = confirmedQtyByLine.get(line);
    const item = getItem(line);
    if (item && confirmed !== undefined) {
      const input = getQtyInput(item);
      // Force the input value even if focused, since we're reverting an invalid entry.
      if (input instanceof HTMLInputElement) input.value = String(confirmed);
      setDisplayedQty(item, confirmed);
    }
    updateGramTrackerFromDom();
  }

  function disableBtnsForLine(line) {
    const item = getItem(line);
    if (!item) return;
    item.querySelectorAll('.ccp-btn').forEach((b) => {
      b.disabled = true;
    });
    const input = getQtyInput(item);
    if (input instanceof HTMLInputElement) input.disabled = true;
  }

  function enableBtnsForLine(line) {
    const item = getItem(line);
    if (!item) return;
    const canUpdate = item.dataset.canUpdateQuantity !== 'false';
    item.querySelectorAll('.ccp-btn--minus, .ccp-btn--plus').forEach((b) => {
      b.disabled = !canUpdate;
    });
    item.querySelectorAll('.ccp-btn--remove').forEach((b) => {
      b.disabled = false;
    });
    const input = getQtyInput(item);
    if (input instanceof HTMLInputElement) input.disabled = !canUpdate;
  }

  function showItemError(line, message) {
    const item = getItem(line);
    if (!item) return;
    const errorEl = item.querySelector('.ccp-item__error');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
    setTimeout(() => {
      errorEl.hidden = true;
    }, 6000);
  }

  function showCanLimitError(el) {
    const item = el && el.closest('.ccp-item');
    const errorEl = item && item.querySelector('.ccp-can-limit-error');
    if (!errorEl) return;

    errorEl.hidden = false;
    setTimeout(() => {
      errorEl.hidden = true;
    }, 6000);
  }

  function getGramLimit() {
    const tracker = document.querySelector('.cart__gram-tracker');
    return tracker ? parseFloat(tracker.dataset.gramLimit || '150') || 150 : 150;
  }

  function showGramLimitError(el) {
    const item = el && el.closest('.ccp-item');
    const errorEl = item && item.querySelector('.ccp-gram-limit-error');
    if (!errorEl) return;

    errorEl.hidden = false;
    setTimeout(() => {
      errorEl.hidden = true;
    }, 6000);
  }

  function getProjectedCanQuantity(line, newTarget) {
    let total = 0;
    getCartItems().forEach((el) => {
      if (el.dataset.isCan !== 'true') return;
      total += getLineQty(el, line, newTarget);
    });
    return total;
  }

  function getProjectedGramTotal(line, newTarget) {
    return computeGramTotalFromDom(line, newTarget);
  }

  /**
   * Max line qty that still fits under the shipping/gram limit, given other
   * lines' confirmed/pending quantities.
   * @returns {number | null}
   */
  function getMaxQtyForGramLimit(line, item) {
    const gramDeduction = parseFloat(item.dataset.gramDeduction || '0');
    if (!(gramDeduction > 0)) return null;

    ensureConfirmed(line, item);
    const confirmed = confirmedQtyByLine.get(line);
    if (confirmed === undefined) return null;

    const baseTotal = getProjectedGramTotal(line, confirmed);
    const headroom = getGramLimit() - baseTotal;
    if (headroom < gramDeduction) return confirmed;

    return confirmed + Math.floor(headroom / gramDeduction);
  }

  /**
   * Max can qty for a line under the 12-can limit.
   * @returns {number | null}
   */
  function getMaxQtyForCanLimit(line, item) {
    if (item.dataset.isCan !== 'true') return null;
    ensureConfirmed(line, item);
    const confirmed = confirmedQtyByLine.get(line);
    if (confirmed === undefined) return null;

    const baseTotal = getProjectedCanQuantity(line, confirmed);
    const headroom = CAN_LIMIT - baseTotal;
    if (headroom <= 0) return confirmed;
    return confirmed + headroom;
  }

  /**
   * Resolve an increase to an allowed target quantity.
   * Applies can + shipping caps, clamps to the binding (lowest) max, and shows
   * the error for whichever limit actually blocks the increase.
   * @returns {number | null}
   */
  function resolveIncreaseTarget(line, item, newTarget, el) {
    ensureConfirmed(line, item);
    const confirmed = confirmedQtyByLine.get(line);
    if (confirmed === undefined) return newTarget;

    const gramDeduction = parseFloat(item.dataset.gramDeduction || '0');
    const maxCan = item.dataset.isCan === 'true' ? getMaxQtyForCanLimit(line, item) : null;
    const maxGram = gramDeduction > 0 ? getMaxQtyForGramLimit(line, item) : null;

    let maxAllowed = newTarget;
    if (maxCan !== null) maxAllowed = Math.min(maxAllowed, maxCan);
    if (maxGram !== null) maxAllowed = Math.min(maxAllowed, maxGram);

    if (maxAllowed >= newTarget) return newTarget;

    // Show the error for the binding constraint. Prefer shipping when tied.
    const gramBlocks = maxGram !== null && newTarget > maxGram;
    const canBlocks = maxCan !== null && newTarget > maxCan;
    if (gramBlocks && (!canBlocks || maxGram <= maxCan)) {
      showGramLimitError(el);
    } else if (canBlocks) {
      showCanLimitError(el);
    } else if (gramBlocks) {
      showGramLimitError(el);
    }

    if (maxAllowed <= confirmed) return null;
    return maxAllowed;
  }

  function scheduleFlush(line) {
    const existing = debounceTimers.get(line);
    if (existing) clearTimeout(existing);

    debounceTimers.set(
      line,
      setTimeout(() => {
        debounceTimers.delete(line);
        flushLine(line);
      }, DEBOUNCE_MS)
    );
  }

  function flushLine(line) {
    if (inFlightLines.has(line)) return;

    const item = getItem(line);
    if (!item) return;

    ensureConfirmed(line, item);
    const confirmed = confirmedQtyByLine.get(line);
    let target = pendingByLine.get(line);

    if (target === undefined || target === confirmed) {
      pendingByLine.delete(line);
      return;
    }

    if (target > confirmed) {
      const resolved = resolveIncreaseTarget(
        line,
        item,
        target,
        item.querySelector('.ccp-btn--plus') || getQtyInput(item)
      );
      if (resolved === null) {
        revertLine(line);
        return;
      }
      if (resolved !== target) {
        target = resolved;
        pendingByLine.set(line, target);
        setDisplayedQty(item, target);
        updateGramTrackerFromDom();
      }
    }

    inFlightLines.add(line);
    disableBtnsForLine(line);

    customElements.whenDefined('cart-items-component').then(() => {
      const cartComp = getCartComp();
      if (!cartComp) {
        inFlightLines.delete(line);
        enableBtnsForLine(line);
        return;
      }

      if (target <= 0) {
        cartComp.onLineItemRemove(line);
      } else {
        cartComp.updateQuantity({ line, quantity: target, action: 'change' });
      }
    });
  }

  function syncConfirmedFromDom(line) {
    const item = getItem(line);
    if (!item) {
      confirmedQtyByLine.delete(line);
      pendingByLine.delete(line);
      return;
    }

    const pending = pendingByLine.get(line);
    if (pending !== undefined) {
      confirmedQtyByLine.set(line, pending);
      setDisplayedQty(item, pending);
      return;
    }

    const qty = parseInt(item.dataset.qty, 10);
    if (!isNaN(qty)) {
      confirmedQtyByLine.set(line, qty);
      setDisplayedQty(item, qty);
    }
  }

  function syncAfterCartChange(line, cart) {
    if (cart) {
      syncLineItemsFromCart(cart);
      return;
    }

    syncConfirmedFromDom(line);
  }

  function maybeFlushPending(line) {
    const item = getItem(line);
    if (!item) return;

    ensureConfirmed(line, item);
    const confirmed = confirmedQtyByLine.get(line);
    const pending = pendingByLine.get(line);
    if (pending !== undefined && pending !== confirmed) scheduleFlush(line);
  }

  /**
   * Commit a typed quantity into the same pending/flush pipeline as +/-.
   * @param {HTMLInputElement} input
   */
  function commitQtyInput(input) {
    if (input.disabled) return;

    const line = parseInt(input.dataset.line, 10);
    if (!line) return;

    const item = input.closest('.ccp-item');
    if (!item) return;

    ensureConfirmed(line, item);
    const confirmed = confirmedQtyByLine.get(line);
    const raw = input.value.trim();

    if (raw === '') {
      revertLine(line);
      return;
    }

    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < 0) {
      revertLine(line);
      return;
    }

    if (parsed === confirmed) {
      pendingByLine.delete(line);
      setDisplayedQty(item, confirmed);
      return;
    }

    let target = parsed;
    if (target > confirmed) {
      const resolved = resolveIncreaseTarget(line, item, target, input);
      if (resolved === null) {
        revertLine(line);
        return;
      }
      target = resolved;
    }

    pendingByLine.set(line, target);
    // Force display even while focused — the typed/clamped value is now committed.
    input.value = String(target);
    setDisplayedQty(item, target);
    updateGramTrackerFromDom();
    scheduleFlush(line);
  }

  document.addEventListener('cart:quantity-error', (e) => {
    const { line, message } = /** @type {CustomEvent} */ (e).detail;
    inFlightLines.delete(line);
    cartResourceForPendingUpdate = null;
    revertLine(line);
    enableBtnsForLine(line);
    showItemError(line, message);
  });

  let cappedLine = /** @type {number | null} */ (null);

  document.addEventListener('cart:quantity-capped', (e) => {
    cappedLine = /** @type {CustomEvent} */ (e).detail.line;
  });

  document.addEventListener('cart:update-complete', (e) => {
    const { line } = /** @type {CustomEvent} */ (e).detail;
    const cartFromUpdate = cartResourceForPendingUpdate;
    cartResourceForPendingUpdate = null;

    inFlightLines.delete(line);
    enableBtnsForLine(line);

    // morphSection is async; sync qty/price from the cart API response because
    // data-skip-subtree-update can preserve stale qty markup.
    syncAfterCartChange(line, cartFromUpdate);
    requestAnimationFrame(() => {
      syncAfterCartChange(line, cartFromUpdate);
    });

    if (cappedLine === line) {
      showItemError(line, 'Maximum quantity reached');
      const item = getItem(line);
      const plusBtn = item && item.querySelector('.ccp-btn--plus');
      if (plusBtn instanceof HTMLElement) plusBtn.disabled = true;
      cappedLine = null;
    }

    maybeFlushPending(line);
  });

  document.addEventListener('cart:update', (e) => {
    const { resource, data } = /** @type {CustomEvent} */ (e).detail;
    if (data?.source !== 'cart-items-component') return;
    if (!getCartList()) return;

    lastCartResource = resource;
    cartResourceForPendingUpdate = resource;
  });

  function removeLine(line) {
    if (inFlightLines.has(line)) return;

    const existing = debounceTimers.get(line);
    if (existing) clearTimeout(existing);
    debounceTimers.delete(line);
    pendingByLine.delete(line);

    inFlightLines.add(line);
    disableBtnsForLine(line);

    customElements.whenDefined('cart-items-component').then(() => {
      const cartComp = getCartComp();
      if (!cartComp) {
        inFlightLines.delete(line);
        enableBtnsForLine(line);
        return;
      }

      cartComp.onLineItemRemove(line);
    });
  }

  function handleClick(e) {
    const btn = e.target.closest('.ccp-btn');
    if (!btn || btn.disabled) return;

    const isRemove = btn.classList.contains('ccp-btn--remove');
    const isMinus = btn.classList.contains('ccp-btn--minus');
    const isPlus = btn.classList.contains('ccp-btn--plus');
    if (!isMinus && !isPlus && !isRemove) return;

    const line = parseInt(btn.dataset.line, 10);
    if (!line) return;

    if (isRemove) {
      removeLine(line);
      return;
    }

    const item = btn.closest('.ccp-item');
    if (!item) return;

    ensureConfirmed(line, item);
    const currentTarget = getCurrentTarget(line, item);
    let newTarget = isMinus ? currentTarget - 1 : currentTarget + 1;

    if (isPlus) {
      const resolved = resolveIncreaseTarget(line, item, newTarget, btn);
      if (resolved === null) return;
      newTarget = resolved;
    }

    pendingByLine.set(line, newTarget);
    setDisplayedQty(item, newTarget);
    updateGramTrackerFromDom();
    scheduleFlush(line);
  }

  function handleQtyFocus(e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('ccp-item__qty-input')) return;
    input.select();
  }

  function handleQtyBlur(e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('ccp-item__qty-input')) return;
    commitQtyInput(input);
  }

  function handleQtyKeydown(e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('ccp-item__qty-input')) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      input.blur();
    }
  }

  function handleQtyChange(e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('ccp-item__qty-input')) return;
    // Covers stepper clicks that change value without an immediate blur.
    commitQtyInput(input);
  }

  document.addEventListener('click', handleClick);
  document.addEventListener('focusin', handleQtyFocus);
  document.addEventListener('focusout', handleQtyBlur);
  document.addEventListener('change', handleQtyChange);
  document.addEventListener('keydown', handleQtyKeydown);
})();
